/**
 * water_fx — the ONE interaction field every water body shares (2026-09-11).
 *
 * A camera-following engine RippleField (a damped 2-D wave equation on a CPU
 * grid) whose texture the sea, the lakes and the river ribbons all sample in
 * WORLD XZ (RealisticWaterMaterialConfig.interaction). Anything that touches
 * water — a stride, a stroke, a hull, a creature, a plunge, a raindrop —
 * calls `stamp()`; AtlasWaterModule owns the tick (origin follow, the wave
 * equation, the uniforms) and registers the surface providers the stampers
 * ask ("is there water here, how high").
 *
 * globalThis-backed singleton (the impact_decals pattern): modules that boot
 * in any order share it; a module reloaded by HMR finds the same field.
 *
 * THE GATE: `enabled` mirrors the graphics flag (Realistic fluid physics).
 * Off, the field never steps and every stamp is a no-op — the texture stays
 * flat and the water renders exactly as before the feature.
 */
import * as THREE from 'three/webgpu';
import { RippleField } from 'enginex/environment/water/RippleField.js';
import { fluidFieldFor, RIPPLE_DAMPING, RIPPLE_SPEED_MPS, type FluidField } from './water_physics.js';

export interface WaterSurfaceProvider {
  /** The STILL water level over (x, z) — sea, lake or river — or null when dry ground. */
  stillAt(x: number, z: number): number | null;
  /** The LIVE surface (waves + ripples) over (x, z), or null when dry. */
  liveAt(x: number, z: number): number | null;
  /** Live surface slope (dh/dx, dh/dz) — a hull rocks on it. */
  slopeAt(x: number, z: number): { dx: number; dz: number };
  /** WHICH water lies over (x, z) — the shore's sound depends on it (waves wash, a lake laps, a river has its own voice). */
  kindAt?(x: number, z: number): 'sea' | 'lake' | 'river' | null;
  /** The sea state's amplitude scale right now (0.35 calm … 1.85 gale; 1 = the authored sea). */
  seaAmp?(): number;
}

export interface WaterFx {
  /** The field (null until a tier with fluid physics built it). */
  field: RippleField | null;
  /** The field's window (tier). */
  spec: FluidField | null;
  /** Shared by every water material: the field's texture + world centre. */
  texture: THREE.Texture;
  size: number;
  /** The gate (the graphics flag). */
  enabled: boolean;
  /** Counters for the probe. */
  stats: { stamps: number; rain: number; steps: number; splashes: number };
  provider: WaterSurfaceProvider | null;
  /** The field's origin in RENDER space (origin + the rebase shift) — every water material's uInteractionCenter. */
  centerRender: { x: number; z: number };
  /** Rain on the water, 0..1 (the sea module's smoothed intensity; rivers copy it). */
  rain: number;
  /** Splash burst hook (AtlasWaterModule installs the emitter). */
  splash: ((x: number, y: number, z: number, count: number, speed: number) => void) | null;
}

const KEY = '__atlasWaterFx';

function placeholderTexture(): THREE.Texture {
  // A 1×1 flat texel: materials built before the field exists (or on tiers
  // without one) sample zero — no ripples, the authored look.
  const data = new Uint16Array(4);
  const tex = new THREE.DataTexture(data, 1, 1, THREE.RGBAFormat, THREE.HalfFloatType);
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** The shared field state (created on first use). */
export function waterFx(): WaterFx {
  const g = globalThis as unknown as Record<string, WaterFx | undefined>;
  let fx = g[KEY];
  if (!fx) {
    fx = {
      field: null, spec: null, texture: placeholderTexture(), size: 64, enabled: false,
      stats: { stamps: 0, rain: 0, steps: 0, splashes: 0 }, provider: null, centerRender: { x: 0, z: 0 }, rain: 0, splash: null,
    };
    g[KEY] = fx;
  }
  return fx;
}

/**
 * Build (or rebuild) the field for a tier. Called by AtlasWaterModule at init
 * and when the tier changes; the materials sample `texture` — the field's
 * own DataTexture once built. Returns the spec (null = no field on this tier).
 */
export function ensureWaterField(tier: string): FluidField | null {
  const fx = waterFx();
  const spec = fluidFieldFor(tier);
  if (!spec) {
    if (fx.field) { fx.field.dispose(); fx.field = null; }
    fx.spec = null;
    return null;
  }
  if (fx.field && fx.spec && fx.spec.res === spec.res && fx.spec.size === spec.size) return spec;
  if (fx.field) fx.field.dispose();
  fx.field = new RippleField({ sizeX: spec.size, sizeZ: spec.size, resolution: spec.res, waveSpeed: RIPPLE_SPEED_MPS, damping: RIPPLE_DAMPING });
  fx.spec = spec;
  fx.size = spec.size;
  fx.texture = fx.field.texture;
  return spec;
}

/** Stamp a disturbance at ABSOLUTE world (x, z); a no-op when the gate is off or the field is absent. */
export function stampWater(x: number, z: number, strength: number, radius: number): boolean {
  const fx = waterFx();
  if (!fx.enabled || !fx.field) return false;
  const o = fx.field.origin;
  const half = fx.size * 0.5 + radius;
  if (Math.abs(x - o.x) > half || Math.abs(z - o.z) > half) return false; // outside the window: nothing to ring
  fx.field.disturbWorld(x, z, strength, radius);
  fx.stats.stamps++;
  return true;
}

/** The still water level over an absolute (x, z), or null on dry ground. */
export function waterStillAt(x: number, z: number): number | null {
  return waterFx().provider?.stillAt(x, z) ?? null;
}

/** The live (waves + ripples) surface over an absolute (x, z), or null on dry ground. */
export function waterLiveAt(x: number, z: number): number | null {
  return waterFx().provider?.liveAt(x, z) ?? null;
}

/** Which water lies over an absolute (x, z): 'sea' | 'lake' | 'river', or null on dry ground (or before the provider exists). */
export function waterKindAt(x: number, z: number): 'sea' | 'lake' | 'river' | null {
  return waterFx().provider?.kindAt?.(x, z) ?? null;
}
/** The sea state's amplitude scale (1 when unknown). */
export function waterSeaAmp(): number { return waterFx().provider?.seaAmp?.() ?? 1; }

/** Live surface slope for a hull to rock on. */
export function waterSlopeAt(x: number, z: number): { dx: number; dz: number } {
  return waterFx().provider?.slopeAt(x, z) ?? { dx: 0, dz: 0 };
}

/** A splash burst at an absolute point (no-op without an emitter or when off). */
export function splashWater(x: number, y: number, z: number, count: number, speed: number): boolean {
  const fx = waterFx();
  if (!fx.enabled || !fx.splash) return false;
  fx.splash(x, y, z, count, speed);
  fx.stats.splashes++;
  return true;
}
